import {
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Line,
  LineChart,
  Tooltip,
} from 'recharts';
import { useScreenView } from '../../../../../../utils/ScreenViewContext';
import {
  IntervalRevenueExpensesData,
  RevenueExpensesData,
} from '../../../../../../types/apiTypes';
import { ClipLoader } from 'react-spinners';
import {
  formatDate,
  getDayWithSuffix,
} from '../../../../../../utils/DateUtils';
import {
  formatCurrency,
  splitCurrencyParts,
} from '../../../../../../utils/CurrencyUtils';
import { DemoDataWrapper } from '../components/DemoDataBadge';

function RevenueAndExpensesWidget({
  showHistorical,
  intraMonth,
  revenueExpensesData,
  loadingRevenueExpensesData,
  selectedUnitNumber,
  isPreActive,
}: {
  showHistorical?: boolean;
  revenueExpensesData: RevenueExpensesData[] | IntervalRevenueExpensesData[];
  loadingRevenueExpensesData: boolean;
  intraMonth?: boolean;
  selectedUnitNumber?: string;
  isPreActive: boolean;
}) {
  const { screenView } = useScreenView();

  const averageRevenue =
    (
      revenueExpensesData as Array<
        RevenueExpensesData | IntervalRevenueExpensesData
      >
    )?.reduce((acc: number, curr) => acc + curr?.revenue, 0) /
    revenueExpensesData?.length;

  const averageExpenses =
    (
      revenueExpensesData as Array<
        RevenueExpensesData | IntervalRevenueExpensesData
      >
    )?.reduce((acc: number, curr) => acc + curr?.expenses, 0) /
    revenueExpensesData?.length;

  const getFilteredData = (): (
    | RevenueExpensesData
    | IntervalRevenueExpensesData
  )[] => {
    if (!intraMonth) return revenueExpensesData;

    const data = revenueExpensesData as IntervalRevenueExpensesData[];
    return data.reduce((acc: IntervalRevenueExpensesData[], curr, index) => {
      if (index % 2 === 0) {
        // If this is the last item and odd number of items, just add it
        if (index === data?.length - 1) {
          acc.push(curr);
          return acc;
        }

        // Get next item to average with
        const next = data[index + 1];

        // Create averaged data point
        const averaged: IntervalRevenueExpensesData = {
          date: curr?.date, // Keep the first date
          revenue: (curr?.revenue + next?.revenue) / 2,
          expenses: (curr?.expenses + next?.expenses) / 2,
        };

        acc.push(averaged);
      }
      return acc;
    }, []);
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    const date = new Date(0); // Create a date object
    date.setUTCMonth(label); // Set the month (0-based index)
    const month = date.toLocaleString('default', { month: 'short' });
    const dayDate = new Date(label);
    if (active && payload && payload.length && !intraMonth) {
      return (
        <div className="rounded-lg bg-gray-800 p-3 text-white shadow-lg">
          <p className="text-[14px] font-semibold">{month}.</p>
          <div className="flex items-center">
            <span
              className="mr-2 inline-block h-3 w-3"
              style={{ backgroundColor: '#6A9E68' }}
            ></span>
            <p className="text-[14px]">
              Revenue: {formatCurrency(payload[0]?.value ?? 0)}
            </p>
          </div>
          <div className="flex items-center">
            <span
              className="mr-2 inline-block h-3 w-3"
              style={{ backgroundColor: '#EF4444' }}
            ></span>
            <p className="text-[14px]">
              Expenses: {formatCurrency(payload[1]?.value ?? 0)}
            </p>
          </div>
        </div>
      );
    } else if (active && payload && payload.length && intraMonth) {
      return (
        <div className="rounded-lg bg-gray-800 p-3 text-white shadow-lg">
          <p className="text-[14px] font-semibold">
            {formatDate(dayDate, false, true)}.
          </p>
          <div className="flex items-center">
            <span
              className="mr-2 inline-block h-3 w-3"
              style={{ backgroundColor: '#6A9E68' }}
            ></span>
            <p className="text-[14px]">
              Revenue: ${payload[0]?.value?.toLocaleString()}
            </p>
          </div>
          <div className="flex items-center">
            <span
              className="mr-2 inline-block h-3 w-3"
              style={{ backgroundColor: '#EF4444' }}
            ></span>
            <p className="text-[14px]">
              Expenses: ${payload[1]?.value?.toLocaleString()}
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  const revenueParts = splitCurrencyParts(averageRevenue);
  const expensesParts = splitCurrencyParts(averageExpenses);

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'tablet':
        return (
          <div className="flex w-[100%] flex-col items-center gap-x-[16px] border-[1px] border-bordergray bg-white align-middle">
            <div className="flex h-full w-full flex-row items-center justify-between border-b-[1px] border-bordergray p-4 align-middle">
              <h2 className="flex items-center text-[16px] font-semibold leading-[24px] text-primary">
                Revenue & Expenses
                {selectedUnitNumber && (
                  <span className="ml-2 block text-sm font-normal text-graysecondary">
                    {selectedUnitNumber}
                  </span>
                )}
              </h2>
              <div className="flex flex-row items-center space-x-4 align-middle">
                <div className="h-4 w-4 rounded-sm bg-secondary"></div>
                <p>Revenue</p>
                <div className="h-4 w-4 rounded-sm bg-red-500"></div>
                <p>Expenses</p>
              </div>
            </div>
            {showHistorical && !loadingRevenueExpensesData && (
              <div className="flex h-[90px] w-full flex-row items-start overflow-x-auto border-b-[1px] border-[#EAECF0] align-middle">
                <div className="flex w-[50%] flex-col items-start gap-y-2 p-3 align-middle">
                  <p className="text-[16px] text-[#7D8191]">
                    {' '}
                    Average Monthly Revenue{' '}
                  </p>
                  <p className="text-[22px] font-semibold leading-[28px] text-primary">
                    ${revenueParts.dollars}.
                    <span className="text-[18px] leading-[28px] text-graysecondary">
                      {revenueParts.cents}
                    </span>
                  </p>
                </div>
                <div className="flex w-[50%] flex-col items-start gap-y-2 border-l-[1px] border-[#EAECF0] p-3 align-middle">
                  <p className="text-[16px] text-[#7D8191]">
                    {' '}
                    Average Monthly Expenses{' '}
                  </p>
                  <p className="text-[22px] font-semibold leading-[28px] text-primary">
                    ${expensesParts.dollars}.
                    <span className="text-[18px] leading-[28px] text-graysecondary">
                      {expensesParts.cents}
                    </span>
                  </p>
                </div>
              </div>
            )}
            {!loadingRevenueExpensesData ? (
              <div className="h-[300px] min-h-[300px] w-full py-2">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    width={500}
                    height={500}
                    data={getFilteredData()}
                    margin={{
                      top: 5,
                      right: 30,
                      left: 35,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey={!intraMonth ? `month` : `date`}
                      tickFormatter={(value) => {
                        if (!intraMonth) {
                          const date = new Date(0);
                          date.setUTCMonth(value);
                          return date.toLocaleString('default', {
                            month: 'short',
                          });
                        } else {
                          const date = new Date(value);
                          date.setMinutes(
                            date.getMinutes() + date.getTimezoneOffset()
                          );
                          return getDayWithSuffix(date);
                        }
                      }}
                    />
                    <YAxis
                      tickFormatter={(value) => `$${value.toLocaleString()}`}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    {/*<Legend />*/}
                    <Line
                      type="monotone"
                      dataKey="revenue"
                      stroke="#6A9E68"
                      activeDot={{ r: 8 }}
                    />
                    <Line type="monotone" dataKey="expenses" stroke="#EF4444" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div
                className={`${!showHistorical ? 'h-[300px] min-h-[300px]' : 'h-[390px] min-h-[390px]'}`}
              >
                <ClipLoader
                  color="#1D213B"
                  loading={loadingRevenueExpensesData}
                  size={60}
                  aria-label="Loading Spinner"
                  data-testid="loader"
                  className="my-10 flex h-full w-full items-center justify-center"
                />
              </div>
            )}
          </div>
        );
      case 'mobile':
        return (
          <div className="flex max-h-[400px] w-[100%] flex-col items-center gap-x-[16px] border-[1px] border-bordergray bg-white align-middle">
            <div className="flex h-full w-full flex-col items-start space-y-[8px] border-b-[1px] border-bordergray p-3 align-middle">
              <h2 className="text-[16px] font-semibold leading-[24px] text-primary">
                Monthly Revenue & Expenses
                {selectedUnitNumber && (
                  <span className="block text-sm font-normal text-graysecondary">
                    {selectedUnitNumber}
                  </span>
                )}
              </h2>
              <div className="flex flex-row items-center space-x-4 align-middle">
                <div className="h-4 w-4 rounded-sm bg-secondary"></div>
                <p>Revenue</p>
                <div className="h-4 w-4 rounded-sm bg-red-500"></div>
                <p>Expenses</p>
              </div>
            </div>
            {!loadingRevenueExpensesData ? (
              <div className="h-[300px] min-h-[300px] w-full py-2">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    width={500}
                    height={500}
                    data={getFilteredData()}
                    margin={{
                      top: 5,
                      right: 20,
                      left: 20,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey={!intraMonth ? `month` : `date`}
                      tickFormatter={(value) => {
                        if (!intraMonth) {
                          const date = new Date(0);
                          date.setUTCMonth(value);
                          return date.toLocaleString('default', {
                            month: 'short',
                          });
                        } else {
                          const date = new Date(value);
                          date.setMinutes(
                            date.getMinutes() + date.getTimezoneOffset()
                          );
                          return getDayWithSuffix(date);
                        }
                      }}
                      label={{
                        value: !intraMonth
                          ? (getFilteredData()[0] as RevenueExpensesData)?.month
                            ? new Date(
                                (getFilteredData()[0] as RevenueExpensesData)
                                  .month
                              ).toLocaleString('default', { month: 'short' }) +
                              '.'
                            : undefined
                          : (
                                getFilteredData()[0] as IntervalRevenueExpensesData
                              )?.date
                            ? new Date(
                                (
                                  getFilteredData()[0] as IntervalRevenueExpensesData
                                ).date
                              ).toLocaleString('default', { month: 'short' }) +
                              '.'
                            : undefined,
                        position: 'insideLeft',
                        angle: 0,
                        dx: -70,
                        dy: 10,
                        style: {
                          textAnchor: 'middle',
                          fill: '#000',
                          fontSize: '14px',
                        },
                      }}
                    />
                    <YAxis
                      tickFormatter={(value) => `$${value.toLocaleString()}`}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    {/*<Legend />*/}
                    <Line
                      type="monotone"
                      dataKey="revenue"
                      stroke="#6A9E68"
                      activeDot={{ r: 8 }}
                    />
                    <Line type="monotone" dataKey="expenses" stroke="#EF4444" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <ClipLoader
                color="#1D213B"
                loading={loadingRevenueExpensesData}
                size={40}
                aria-label="Loading Spinner"
                data-testid="loader"
                className="my-10 flex h-full w-full items-center justify-center"
              />
            )}
          </div>
        );
    }
  };

  return (
    <DemoDataWrapper showBadge={isPreActive}>
      {renderScreenView(screenView)}
    </DemoDataWrapper>
  );
}

export default RevenueAndExpensesWidget;
